import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import {
  useCloneProductMutation,
  useCreateOrUpdateProductMutation,
  useDeleteProductByIdMutation,
  useGetAllProductQuery,
} from "../../services/product";
import { CopyOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { Product } from "../../types/product";
import { useEffect, useState } from "react";
import GenericDrawer from "../drawer";
import ProductForm from "./forms/createUpdateProductForm";
import { useNavigate } from "react-router-dom";
import { generateCodeToPid } from "../../ultils/helpers";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import MonoCell from "../mono-cell";

const ProductManagementContent = () => {
  const { data: products } = useGetAllProductQuery();
  const user = useSelector((state: RootState) => state.user.user);
  const [createOrUpdateProduct, { data: createOrUpdateProductData }] =
    useCreateOrUpdateProductMutation();
  const [deleteProduct] = useDeleteProductByIdMutation();
  const [cloneProduct] = useCloneProductMutation();
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [cloneSource, setCloneSource] = useState<Product | null>(null);
  const [cloneForm] = Form.useForm();
  const handleClickDeleteProduct = (id: string) => {
    deleteProduct(id);
  };
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const openDrawer = (record: Product | null) => {
    setSelectedProduct(
      record
        ? record
        : {
            _id: undefined as any,
            device_type: undefined as any,
            product_name: undefined as any,
            protocol_type: undefined as any,
            pid: generateCodeToPid(),
            createdAt: undefined as any,
            updatedAt: undefined as any,
          }
    );
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
  };

  const handleSubmit = async (values: Product) => {
    values._id = selectedProduct?._id!;
    await createOrUpdateProduct(values);
    closeDrawer();
  };

  const openCloneModal = (record: Product) => {
    setCloneSource(record);
    cloneForm.setFieldsValue({
      pid: generateCodeToPid(),
      product_name: `${record.product_name} (Copy)`,
    });
    setCloneModalVisible(true);
  };

  const handleCloneSubmit = async () => {
    const values = await cloneForm.validateFields();
    let retries = 5;
    while (retries--) {
      try {
        await cloneProduct({
          sourceId: cloneSource!._id,
          pid: values.pid,
          product_name: values.product_name,
        }).unwrap();
        messageApi.success("Clone product successfully");
        setCloneModalVisible(false);
        return;
      } catch (err: any) {
        if (err?.status === 409 && retries > 0) {
          // PID trùng → regenerate PID và thử lại
          values.pid = generateCodeToPid();
          cloneForm.setFieldsValue({ pid: values.pid });
          continue;
        }
        messageApi.error("Clone thất bại");
        return;
      }
    }
  };

  useEffect(() => {
    if (createOrUpdateProductData) {
      messageApi.success("Thay mới sản phẩm thành công");
    }
  }, [createOrUpdateProductData, messageApi]);

  const columns = [
    {
      title: "PID",
      dataIndex: "pid",
      key: "pid",
      render: (text: string) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Device type",
      dataIndex: "device_type",
      key: "device_type",
    },
    {
      title: "Protocol",
      dataIndex: "protocol_type",
      key: "protocol_type",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string | number | Date) => (
        <>{new Date(text).toLocaleString()}</>
      ),
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string | number | Date) => (
        <>{new Date(text).toLocaleString()}</>
      ),
    },
    {
      title: "",
      key: "action",
      render: (record: Product) => (
        <Space size="middle">
          <Button
            onClick={() =>
              navigate(`product_detail/${record._id}`, { state: record })
            }
            type="link"
            icon={<EyeOutlined />}
          >
            Xem
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => openCloneModal(record)}
          >
            Clone
          </Button>
          {user?.role === 3 ? (
            <Popconfirm
              title="Xoá sản phẩm"
              description="Bạn có chắc chắn xoá sản phẩm này"
              onConfirm={() => handleClickDeleteProduct(record._id)}
              okText="Có"
              cancelText="Không"
            >
              <Button type="link" danger>
                Xoá
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div className="container" style={{ padding: "0 16px" }}>
      {contextHolder}

      {/* Header + Button */}
      <div className="page-head">
        <h1>Quản lý sản phẩm</h1>
        <Button type="primary" onClick={() => openDrawer(null)}>
          Thêm sản phẩm
        </Button>
      </div>

      {/* Drawer */}
      <GenericDrawer
        width={600}
        visible={isDrawerVisible}
        onClose={closeDrawer}
        title={selectedProduct ? "Cập nhật sản phẩm" : "Thêm mới sản phẩm"}
        formComponent={ProductForm}
        formProps={{
          initialValues: selectedProduct,
          onSubmit: handleSubmit,
        }}
      />

      {/* Search form */}
      {/* <div className="search_product" style={{ marginBottom: "24px" }}>
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="pid">
                <Input placeholder="PID" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="product_name">
                <Input placeholder="Tên sản phẩm" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="protocol_type">
                <Select
                  placeholder="Protocol"
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  // options={optionTenPhongBans}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6} md={4}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
                block
              >
                Tìm kiếm
              </Button>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Button block>Làm mới</Button>
            </Col>
          </Row>
        </Form>
      </div> */}

      {/* Table */}
      <Table
        columns={columns}
        dataSource={products}
        rowKey="_id"
        scroll={{ x: "max-content" }}
      />

      {/* Clone Modal */}
      <Modal
        title="Clone"
        open={cloneModalVisible}
        onOk={handleCloneSubmit}
        onCancel={() => setCloneModalVisible(false)}
        okText="Clone"
        cancelText="Huỷ"
      >
        <Form form={cloneForm} layout="vertical">
          <Form.Item name="pid" label="PID">
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="product_name"
            label="Tên sản phẩm"
            rules={[{ required: true, message: "Vui lòng nhập tên sản phẩm" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManagementContent;
